import { Context, ReactNode, createContext, useContext } from "react";
import { SkpEnvironment } from "../entities";

type SkpEnvironmentProviderProp = {
  children: ReactNode;
  environment: SkpEnvironment;
};

const SkpEnvironmentContext: Context<SkpEnvironment | undefined> =
  createContext<SkpEnvironment | undefined>(undefined);

const useSkpEnvironment = (): SkpEnvironment | undefined =>
  useContext(SkpEnvironmentContext);

/**
 * Provider wrapper which provides `environment` value
 * down the component tree.
 *
 * @param environmentProviderProp
 * @returns an skp-light environment provider wrapper component.
 */
const EnvironmentProvider = (
  environmentProviderProp: SkpEnvironmentProviderProp
): JSX.Element => {
  return (
    <SkpEnvironmentContext.Provider value={environmentProviderProp.environment}>
      {environmentProviderProp.children}
    </SkpEnvironmentContext.Provider>
  );
};

type conditionalEnvironmentProviderProp = {
  condition: boolean;
  environment: SkpEnvironment | undefined;
  children: ReactNode;
};

const ConditionalEnvironmentProvider = ({
  condition,
  environment,
  children,
}: conditionalEnvironmentProviderProp): JSX.Element => {
  return condition ? (
    <EnvironmentProvider environment={environment!}>
      {children}
    </EnvironmentProvider>
  ) : (
    <>{children}</>
  );
};

export {
  useSkpEnvironment,
  EnvironmentProvider,
  ConditionalEnvironmentProvider,
};
